import type {
  DesignNode,
  MeshSurface,
  PlacementPosition,
  ShapedComponent,
} from '@repo/api-contracts';
import { createRaster, type Raster } from '@repo/warp-engine';
import type { Point2 } from '../geometry/matrix';
import { compositeOver } from '../raster/composite';
import { rasterizePolygonMask } from '../raster/polygon-mask';
import { applyShapeMask } from '../raster/shape-mask';
import { drawRasterTriangle } from '../raster/triangle-raster';
import { meshMmToPhoto, meshScale } from '../surface/mesh-mapping';
import type { PhotoDimensions } from '../types';
import { renderComponent } from './render-component';
import type { FacePreviewInput } from './render-face-preview';

/** 격자 칸 하나를 가로·세로로 나누는 작은 사각형 수. 작은 사각형마다 삼각형 두 개(아핀)로 양선형 휨을 근사한다. */
export const MESH_CELL_SUBDIVISIONS = 8;

interface Quad {
  /** 원본(컴포넌트 래스터) px 꼭짓점 TL·TR·BR·BL. */
  readonly src: readonly [Point2, Point2, Point2, Point2];
  /** 사진 px 꼭짓점 TL·TR·BR·BL. */
  readonly dst: readonly [Point2, Point2, Point2, Point2];
}

/** 0..count 등분선의 k 번째 값 — 이웃 작은 사각형이 같은 식으로 같은 경계값을 얻는다. */
const line = (k: number, count: number, size: number): number => (k * size) / count;

/**
 * 격자 표면 위 배치 하나를 그린다. 컴포넌트를 펼친 래스터로 그린 뒤, 배치 mm 사각형과 겹치는 작은 사각형마다
 * mm 꼭짓점을 meshMmToPhoto 로 옮겨 삼각형 두 개(TL-TR-BR, TL-BR-BL)로 휘어 그린다. 격자(실측 프레임) 밖은 그리지 않는다.
 * 외접 상자 크기 임시 래스터에 그리고, 표면 윤곽이 있으면 그 다각형 마스크를 곱한 뒤 layer 에 source-over 로 올린다.
 */
export const drawMeshPlacement = (
  layer: Raster,
  surface: MeshSurface,
  photo: PhotoDimensions,
  input: Pick<FacePreviewInput, 'materials' | 'createContext' | 'measure'>,
  component: ShapedComponent,
  layers: readonly DesignNode[],
  position: PlacementPosition,
): void => {
  const scaleOf = meshScale(surface, photo);
  const pxPerMm = Math.max(scaleOf.x, scaleOf.y);
  if (!(pxPerMm > 0)) return;
  const comp = renderComponent({
    component,
    layers,
    materials: input.materials,
    pxPerMm,
    createContext: input.createContext,
    measure: input.measure,
  });

  const left = position.xMm;
  const top = position.yMm;
  const right = position.xMm + component.widthMm;
  const bottom = position.yMm + component.heightMm;
  const columnsMm = (surface.columns - 1) * MESH_CELL_SUBDIVISIONS;
  const rowsMm = (surface.rows - 1) * MESH_CELL_SUBDIVISIONS;
  const { widthMm, heightMm } = surface.realSize;

  const quads: Quad[] = [];
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  const corner = (xMm: number, yMm: number): { src: Point2; dst: Point2 } => {
    const dst = meshMmToPhoto(surface, photo, { xMm, yMm });
    minX = Math.min(minX, dst.x);
    minY = Math.min(minY, dst.y);
    maxX = Math.max(maxX, dst.x);
    maxY = Math.max(maxY, dst.y);
    return { src: { x: (xMm - left) * pxPerMm, y: (yMm - top) * pxPerMm }, dst };
  };

  for (let j = 0; j < rowsMm; j += 1) {
    const y0 = Math.max(top, line(j, rowsMm, heightMm));
    const y1 = Math.min(bottom, line(j + 1, rowsMm, heightMm));
    if (!(y1 > y0)) continue;
    for (let i = 0; i < columnsMm; i += 1) {
      const x0 = Math.max(left, line(i, columnsMm, widthMm));
      const x1 = Math.min(right, line(i + 1, columnsMm, widthMm));
      if (!(x1 > x0)) continue;
      const tl = corner(x0, y0);
      const tr = corner(x1, y0);
      const br = corner(x1, y1);
      const bl = corner(x0, y1);
      quads.push({ src: [tl.src, tr.src, br.src, bl.src], dst: [tl.dst, tr.dst, br.dst, bl.dst] });
    }
  }
  if (quads.length === 0) return;

  const originX = Math.max(0, Math.floor(minX));
  const originY = Math.max(0, Math.floor(minY));
  const width = Math.min(layer.width, Math.ceil(maxX)) - originX;
  const height = Math.min(layer.height, Math.ceil(maxY)) - originY;
  if (width <= 0 || height <= 0) return;
  const offset = { x: originX, y: originY };
  const warped = createRaster(width, height);
  for (const { src, dst } of quads) {
    drawRasterTriangle(warped, comp, [src[0], src[1], src[2]], [dst[0], dst[1], dst[2]], offset);
    drawRasterTriangle(warped, comp, [src[0], src[2], src[3]], [dst[0], dst[2], dst[3]], offset);
  }

  if (surface.outline !== undefined) {
    const outline = surface.outline.points.map((point) => ({
      x: (point.x * photo.widthPx) / 100,
      y: (point.y * photo.heightPx) / 100,
    }));
    applyShapeMask(warped, rasterizePolygonMask(outline, width, height, offset));
  }
  compositeOver(layer, warped, originX, originY);
};
